<script setup lang="ts">
  import { isNullable } from "@krainovsd/js-helpers";
  import { VButton, VSelect } from "@krainovsd/vue-ui";
  import { computed, ref, watch } from "vue";
  import { TARGET_BY_VALUE } from "@/entities/comparison";
  import type { ComparisonMode, ComparisonTarget } from "@/entities/comparison";
  import Field from "@/components/atoms/Field.vue";
  import ArrowLeftRight from "@/components/icons/ArrowLeftRight.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const props = defineProps<{ target: ComparisonTarget; mode: ComparisonMode }>();

  // The selected object names (what the comparison record stores / displays).
  const valueOne = defineModel<string>("valueOne", { default: "" });
  const valueTwo = defineModel<string>("valueTwo", { default: "" });
  const emit = defineEmits<{ next: []; back: [] }>();

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  const targetLabel = computed(() => TARGET_BY_VALUE[props.target].label);

  // Loaded items for the current target.
  const options = ref<{ id: number; name: string }[]>([]);
  const loading = ref(false);
  const selectOptions = computed(() =>
    options.value.map((option) => ({ label: option.name, value: option.id })),
  );
  const nameById = computed(() => new Map(options.value.map((o) => [o.id, o.name])));

  // Internal id-based selection; the public model exposes the resolved names.
  const idOne = ref<number | undefined>(undefined);
  const idTwo = ref<number | undefined>(undefined);

  watch(idOne, (id) => (valueOne.value = isNullable(id) ? "" : (nameById.value.get(id) ?? "")));
  watch(idTwo, (id) => (valueTwo.value = isNullable(id) ? "" : (nameById.value.get(id) ?? "")));

  // Reload the list whenever the target changes, clearing any prior selection.
  watch(
    () => props.target,
    async (target) => {
      idOne.value = undefined;
      idTwo.value = undefined;
      loading.value = true;
      try {
        options.value = await TARGET_BY_VALUE[target].fetch();
      } catch {
        options.value = [];
      } finally {
        loading.value = false;
      }
    },
    { immediate: true },
  );

  // "previous_period" needs one object; "anotherTarget" needs two distinct ones.
  const valid = computed(() => {
    if (props.mode === "previous_period") return !isNullable(idOne.value);
    return !isNullable(idOne.value) && !isNullable(idTwo.value) && idOne.value !== idTwo.value;
  });

  // Swap object A and B.
  function reverse() {
    const next = idOne.value;
    idOne.value = idTwo.value;
    idTwo.value = next;
  }
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="
      $t(
        'page.createComparison.step2.title',
        { target: targetLabel },
        { default: 'Выберите {target}' },
      )
    "
    :subtitle="
      props.mode === 'previous_period'
        ? $t(
            'page.createComparison.step2.subtitlePrevious',
            {},
            { default: 'Сравним объект с его прошлым периодом' },
          )
        : $t(
            'page.createComparison.step2.subtitleAnother',
            {},
            { default: 'Сравним метрики объекта A и объекта B' },
          )
    "
    back
    next
    :disabled="!valid"
    @back="emit('back')"
    @next="emit('next')"
  >
    <template v-if="props.mode === 'previous_period'">
      <Field :label="targetLabel">
        <VSelect
          v-model="idOne"
          :options="selectOptions"
          :loading="loading"
          search
          clear
          :placeholder="
            $t('page.createComparison.step2.placeholder', {}, { default: 'Не выбрано' })
          "
        />
      </Field>
    </template>

    <template v-else>
      <Field :label="`${targetLabel} A`">
        <VSelect
          v-model="idOne"
          :options="selectOptions"
          :loading="loading"
          search
          clear
          :placeholder="
            $t('page.createComparison.step2.placeholder', {}, { default: 'Не выбрано' })
          "
        />
      </Field>
      <div :class="$style.reverse">
        <VButton
          type="default"
          size="large"
          :aria-label="
            $t('page.createComparison.step2.reverse', {}, { default: 'Поменять местами' })
          "
          @click="reverse"
        >
          <ArrowLeftRight :size="18" />
        </VButton>
      </div>
      <Field :label="`${targetLabel} B`">
        <VSelect
          v-model="idTwo"
          :options="selectOptions"
          :loading="loading"
          search
          clear
          :placeholder="
            $t('page.createComparison.step2.placeholder', {}, { default: 'Не выбрано' })
          "
        />
      </Field>
    </template>
  </StepLayout>
</template>

<style lang="scss" module>
  .reverse {
    display: flex;
    justify-content: center;
  }
</style>
